import {
  forwardRef,
  Fragment,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Button,
  DropDown,
  Flex,
  FlexColumn,
  FlexSpace,
  FormControls,
  IconButton,
  LoadingButton,
  SvgIcon,
  TextField,
} from '@elementary/common-ui/components';
import { Box, Tooltip, Typography } from '@mui/material';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import { ReactComponent as NullableIcon } from '@elementary/common-ui/assets/20x20/nullable.svg';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import { DataContractFormValues } from '@elementary/common-ui/types';
import styled from 'styled-components';
import { enforcementLevelOptions } from 'shared/components/AddTestWizard/constants';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';

const AccordionHeader = styled(FlexSpace)`
  width: 100%;
  height: 24px;
`;

const SaveButton = styled(LoadingButton)`
  width: fit-content;
  margin: ${space(0, 0, 0, 'auto')};
`;

const ColumnsListScroll = styled(Box)<{ $readOnly?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr auto ${({ $readOnly }) =>
      $readOnly ? '' : 'auto'};
  column-gap: ${({ theme }) => theme.spacing(1)};
  row-gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  max-height: 400px;
  overflow-y: auto;
`;

const SectionTitle = styled(Typography)`
  font-size: ${fontSize('base')};
  font-weight: ${fontWeight('semibold')};
  color: ${({ theme }) => theme.palette.content.primary};
`;

const CaptureSchemaButton = styled(LoadingButton)`
  color: ${({ theme }) => theme.palette.content.primary};
  border-color: ${({ theme }) => theme.palette.border.strong};
`;

const TrashWrapper = styled(Flex)<{ $visible: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s;
`;

const EmptyStateText = styled(Typography).attrs({ variant: 'body2' })`
  color: ${({ theme }) => theme.palette.content.secondary};
`;

type AccordionSummaryProps = {
  title: ReactNode;
  onSave: () => void;
  isLoading: boolean;
  isExpanded: boolean;
  readOnly?: boolean;
};

const AccordionSummary = ({
  title,
  onSave,
  isLoading,
  isExpanded,
  readOnly,
}: AccordionSummaryProps) => (
  <AccordionHeader>
    <Typography sx={{ lineHeight: 0 }}>{title}</Typography>
    {isExpanded && !readOnly && (
      <SaveButton
        loading={isLoading}
        onClick={(e) => {
          onSave();
          e.stopPropagation();
        }}
        size="extraSmall"
      >
        Save changes
      </SaveButton>
    )}
  </AccordionHeader>
);

type Props = {
  config: DataContractFormValues;
  isLoading: boolean;
  onSave?: () => void;
  onCapture?: () => Promise<DataContractFormValues['baselineColumns'] | null>;
  readOnly?: boolean;
};

const DataContractTestConfigurationFields = forwardRef(
  ({ config, isLoading, onSave, onCapture, readOnly }: Props, ref) => {
    const methods = useForm<DataContractFormValues>({
      defaultValues: config,
    });

    const [testSettingsExpanded, setTestSettingsExpanded] = useState(true);
    const [isCapturingSchema, setIsCapturingSchema] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
      methods,
    }));

    const { onPanelConfigChanged } = useSidePanelParams();

    useEffect(() => {
      onPanelConfigChanged(methods.formState.isDirty);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [methods.formState.isDirty]);

    const { fields, append, remove } = useFieldArray({
      control: methods.control,
      name: 'baselineColumns',
    });

    const handleCaptureCurrentSchema = async () => {
      if (!onCapture) return;
      setIsCapturingSchema(true);
      try {
        const capturedColumns = await onCapture();
        if (!capturedColumns) return;
        methods.setValue('baselineColumns', capturedColumns, {
          shouldDirty: true,
        });
      } finally {
        setIsCapturingSchema(false);
      }
    };

    return (
      <FlexColumn sx={{ gap: 3, overflowY: 'auto' }}>
        <FormControls.TestAccordion
          summary={
            <AccordionSummary
              title="Test Settings"
              isLoading={isLoading}
              onSave={() => onSave?.()}
              isExpanded={testSettingsExpanded}
              readOnly={readOnly}
            />
          }
          expanded={testSettingsExpanded}
          onChange={(_, expanded) => setTestSettingsExpanded(expanded)}
        >
          <FlexColumn sx={{ gap: 3, p: 3 }}>
            <FormControls.FieldsWrapper>
              <FlexSpace sx={{ alignItems: 'center' }}>
                <SectionTitle>Baseline columns</SectionTitle>

                {!readOnly && (
                  <CaptureSchemaButton
                    size="medium"
                    variant="outlined"
                    onClick={handleCaptureCurrentSchema}
                    loading={isCapturingSchema}
                    disabled={!onCapture}
                  >
                    Capture current schema
                  </CaptureSchemaButton>
                )}
              </FlexSpace>

              <ColumnsListScroll
                $readOnly={readOnly}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {fields.map((field, index) => {
                  const isNullable =
                    methods.watch(`baselineColumns.${index}.is_nullable`) ??
                    true;
                  return (
                    <Fragment key={field.id}>
                      <TextField
                        size="large"
                        placeholder="Column name"
                        disabled={readOnly}
                        onMouseEnter={() => setHoveredIndex(index)}
                        {...methods.register(
                          `baselineColumns.${index}.column_name`,
                        )}
                      />
                      <TextField
                        size="large"
                        placeholder="Data type"
                        disabled={readOnly}
                        onMouseEnter={() => setHoveredIndex(index)}
                        {...methods.register(
                          `baselineColumns.${index}.data_type`,
                        )}
                      />

                      <Tooltip title="Nullable">
                        <IconButton
                          size="small"
                          variant="outlined"
                          disabled={readOnly}
                          sx={{
                            borderColor: isNullable
                              ? 'border.strong'
                              : 'border.translucent',
                          }}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onClick={() =>
                            methods.setValue(
                              `baselineColumns.${index}.is_nullable`,
                              !isNullable,
                              { shouldDirty: true },
                            )
                          }
                        >
                          <SvgIcon
                            strokeColor={
                              isNullable ? 'icon.primary' : 'icon.secondary'
                            }
                          >
                            <NullableIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>

                      {!readOnly && (
                        <Tooltip title="Delete">
                          <TrashWrapper
                            $visible={hoveredIndex === index}
                            onMouseEnter={() => setHoveredIndex(index)}
                          >
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                            >
                              <TrashIcon />
                            </IconButton>
                          </TrashWrapper>
                        </Tooltip>
                      )}
                    </Fragment>
                  );
                })}
                {fields.length === 0 && (
                  <EmptyStateText sx={{ gridColumn: '1 / -1' }}>
                    No baseline columns defined yet.
                  </EmptyStateText>
                )}

                {!readOnly && (
                  <Box sx={{ gridColumn: '1 / 3' }}>
                    <Button
                      variant="outlined"
                      size="medium"
                      fullWidth
                      onClick={() =>
                        append({
                          column_name: '',
                          data_type: '',
                          is_nullable: true,
                        })
                      }
                    >
                      + Add column
                    </Button>
                  </Box>
                )}
              </ColumnsListScroll>
            </FormControls.FieldsWrapper>

            <FormControls.Divider />

            <FormControls.FieldsWrapper>
              <Controller
                name="enforcementLevel"
                control={methods.control}
                render={({ field }) => (
                  <FormControls.Row>
                    <Tooltip
                      title={
                        <>
                          <div>
                            <strong>All changes -- </strong>fail on any schema
                            deviation, including new columns.
                          </div>
                          <div>
                            <strong>Breaking changes only --</strong> fail on
                            column removals and type/nullability changes, but
                            tolerate new columns.
                          </div>
                          <div>
                            <strong>Downstream impact only --</strong> fail only
                            when the changed column has downstream dependencies.
                          </div>
                          <div>
                            <strong>Critical downstream impact only -- </strong>
                            fail only when the changed column has critical
                            downstream dependencies.
                          </div>
                        </>
                      }
                    >
                      <Typography sx={{ fontWeight: fontWeight('semibold') }}>
                        Enforcement level
                      </Typography>
                    </Tooltip>
                    <DropDown
                      size="medium"
                      disabled={readOnly}
                      options={enforcementLevelOptions}
                      value={field.value || 'all'}
                      onChange={field.onChange}
                    />
                  </FormControls.Row>
                )}
              />
            </FormControls.FieldsWrapper>
          </FlexColumn>
        </FormControls.TestAccordion>
      </FlexColumn>
    );
  },
);

export default DataContractTestConfigurationFields;
